import store from "../store";

const CONSENT_COOKIE = "cookieConsent";
const CONSENT_TIMESTAMP_COOKIE = "cookieConsentTimestamp";
const CONSENT_DAYS = 180;

const getActiveConfig = () => {
  const { currentConfig, defaultConfig } = store.state.config;
  return currentConfig || defaultConfig || {};
};

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

if (typeof window.gtag !== "function") {
  window.gtag = function gtag() {
    ensureDataLayer();
    window.dataLayer.push(arguments);
  };
}

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
  return null;
};

export const setCookie = (name, value, days) => {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  const domain = window.location.hostname;
  if (domain.includes(".")) {
    const parts = domain.split(".");
    if (parts.length >= 2) {
      const mainDomain = `.${parts.slice(-2).join(".")}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${mainDomain}`;
    }
  }
};

export const isCookiesAllowed = (config = getActiveConfig()) => !config?.cookieFree;

const normalizeAnalyticsId = (value) =>
  typeof value === "string" ? value.trim() : "";

export const hasGoogleAnalyticsConfigured = (config = getActiveConfig()) =>
  Boolean(
    normalizeAnalyticsId(config?.googleTagManagerId) ||
      normalizeAnalyticsId(config?.googleAnalyticsMeasurementId),
  );

export const getConsentChoice = () => {
  const choice = getCookie(CONSENT_COOKIE);
  const timestamp = getCookie(CONSENT_TIMESTAMP_COOKIE);
  if (!choice || !timestamp) {
    return null;
  }
  const ageMs = Date.now() - parseInt(timestamp, 10);
  const maxAgeMs = CONSENT_DAYS * 24 * 60 * 60 * 1000;
  if (!Number.isFinite(ageMs) || ageMs > maxAgeMs) {
    return null;
  }
  if (choice === "granted" || choice === "denied") {
    return choice;
  }
  return null;
};

export const hasAnalyticsConsent = (config = getActiveConfig()) =>
  hasGoogleAnalyticsConfigured(config) &&
  isCookiesAllowed(config) &&
  getConsentChoice() === "granted";

export const getAnalyticsConsentHeaderValue = () => getConsentChoice() || "";

/**
 * Normalize Cultbooking-style and Gloveler consent values to granted|denied.
 * Accepts: accepted|granted|true|1 and rejected|denied|false|0
 */
export const normalizeConsentValue = (value) => {
  if (value == null) {
    return null;
  }
  const normalized = String(value).trim().toLowerCase();
  if (["accepted", "granted", "true", "1", "yes"].includes(normalized)) {
    return "granted";
  }
  if (["rejected", "denied", "false", "0", "no"].includes(normalized)) {
    return "denied";
  }
  return null;
};

export const toParentConsentValue = (choice) => {
  if (choice === "granted") {
    return "accepted";
  }
  if (choice === "denied") {
    return "rejected";
  }
  return null;
};

const isEmbeddedInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (_error) {
    return true;
  }
};

/**
 * Notify a parent hotel site when consent changes inside the Gloveler iframe.
 */
export const notifyParentCookieConsent = (choice) => {
  if (!isEmbeddedInIframe()) {
    return;
  }
  const parentValue = toParentConsentValue(choice);
  if (!parentValue) {
    return;
  }
  try {
    window.parent.postMessage({ cookieConsent: parentValue }, "*");
  } catch (_error) {
    // Ignore cross-origin parent access errors.
  }
};

const deleteGoogleCookies = () => {
  const patterns = [
    "_ga",
    "_gid",
    "_gat",
    "_gcl_au",
    "_gac_",
    "_dc_gtm_",
    "_gcl_aw",
    "_gcl_dc",
    "_gads",
  ];

  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (
      patterns.some(
        (pattern) =>
          name === pattern ||
          name.startsWith(`${pattern}_`) ||
          name.toLowerCase().includes("google") ||
          name.toLowerCase().includes("gtm") ||
          name.toLowerCase().includes("analytics"),
      )
    ) {
      deleteCookie(name);
    }
  });
};

export const initializeConsentDefaults = (config = getActiveConfig()) => {
  if (!hasGoogleAnalyticsConfigured(config)) {
    return;
  }

  ensureDataLayer();
  window.gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
    url_passthrough: true,
    region: "eu",
  });
};

export const updateConsentState = (
  consentChoice,
  config = getActiveConfig(),
  { notifyParent = true } = {},
) => {
  if (consentChoice !== "granted" && consentChoice !== "denied") {
    return;
  }

  setCookie(CONSENT_COOKIE, consentChoice, CONSENT_DAYS);
  setCookie(CONSENT_TIMESTAMP_COOKIE, String(Date.now()), CONSENT_DAYS);

  if (consentChoice === "granted") {
    if (!isCookiesAllowed(config)) {
      return;
    }
    window.gtag("consent", "update", {
      ad_storage: "granted",
      analytics_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
    });
    document.dispatchEvent(new Event("consentGranted"));
    if (notifyParent) {
      notifyParentCookieConsent("granted");
    }
    return;
  }

  deleteGoogleCookies();
  window.gtag("consent", "update", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  document.dispatchEvent(new Event("consentDenied"));
  if (notifyParent) {
    notifyParentCookieConsent("denied");
  }
};

/**
 * Apply consent coming from a parent hotel site or URL param.
 */
export const applyExternalCookieConsent = (
  rawValue,
  config = getActiveConfig(),
  options = {},
) => {
  const choice = normalizeConsentValue(rawValue);
  if (!choice) {
    return false;
  }
  updateConsentState(choice, config, { notifyParent: false, ...options });
  return true;
};

/**
 * Restore a previous choice (if valid) and sync Consent Mode. Returns true when a choice exists.
 */
export const restorePreviousConsent = (config = getActiveConfig()) => {
  if (!hasGoogleAnalyticsConfigured(config)) {
    return false;
  }

  if (!isCookiesAllowed(config)) {
    updateConsentState("denied", config, { notifyParent: false });
    return true;
  }

  const choice = getConsentChoice();
  if (!choice) {
    return false;
  }

  updateConsentState(choice, config, { notifyParent: false });
  return true;
};

export const shouldShowConsentBanner = (config = getActiveConfig()) =>
  hasGoogleAnalyticsConfigured(config) &&
  isCookiesAllowed(config) &&
  !getConsentChoice();

const readConsentFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("cookieConsent");
  } catch (_error) {
    return null;
  }
};

/**
 * Listen for parent postMessage({ cookieConsent }) and optional ?cookieConsent= URL.
 * Cultbooking-compatible: accepted|rejected (also granted|denied).
 */
export const initIframeConsentBridge = (config = getActiveConfig()) => {
  if (!hasGoogleAnalyticsConfigured(config)) {
    return () => {};
  }

  const fromUrl = readConsentFromUrl();
  if (fromUrl) {
    applyExternalCookieConsent(fromUrl, config);
  }

  const onMessage = (event) => {
    if (!event?.data || typeof event.data !== "object") {
      return;
    }
    if (!Object.prototype.hasOwnProperty.call(event.data, "cookieConsent")) {
      return;
    }
    // Only accept consent messages from the embedding parent (hotel site).
    if (event.source !== window.parent) {
      return;
    }
    applyExternalCookieConsent(event.data.cookieConsent, getActiveConfig());
  };

  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
};

// Keep Cultbooking-compatible global for any external scripts.
window.hasAnalyticsConsent = () => hasAnalyticsConsent();
